/**
 * WebSocket connection for real-time events (API §3).
 * Connects when authenticated, subscribes to current studio (or all studios when "all").
 * Dispatches custom events so Chats/Dashboard can refetch without polling.
 */
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { getAccessToken } from "@/lib/api-client";
import { runProactiveRefreshIfNeeded } from "@/lib/auth-refresh-coordinator";
import { encodeWsBearerSubprotocol } from "@/lib/ws-auth";
import {
  TOKEN_REFRESHED_EVENT,
  WS_AGENT_WORKFLOW_EVENT,
  WS_CONNECTION_HEALTH_EVENT,
  WS_CONVERSATION_EVENT,
  WS_DASHBOARD_EVENT,
  WS_SESSION_EVENT,
  WS_SYNC_EVENT,
} from "@/lib/ws-events";
import { getWebSocketUrl } from "@/lib/ws-url";
import { useAuth } from "./AuthContext";
import { useStudio } from "./StudioContext";

export {
  WS_AGENT_WORKFLOW_EVENT,
  WS_CONNECTION_HEALTH_EVENT,
  WS_CONVERSATION_EVENT,
  WS_DASHBOARD_EVENT,
  WS_SESSION_EVENT,
  WS_SYNC_EVENT,
} from "@/lib/ws-events";

const CONVERSATION_EVENT_TYPES = new Set([
  "message.new",
  "message.updated",
  "message.delivery_failed",
  "draft.ready",
  "draft.failed",
  "conversation.new",
  "conversation.updated",
  "conversation.status_changed",
  "conversation.archived",
  "conversation.restored",
  "conversation.purged",
  "conversation.identity_approved",
]);

const SESSION_EVENT_TYPES = new Set(["session.scope_changed"]);

const SYNC_EVENT_TYPES = new Set([
  "sync_progress",
  "sync_completed",
  "sync_failed",
]);
const AGENT_WORKFLOW_EVENT_TYPES = new Set(["agent_workflow_progress"]);

interface WebSocketContextType {
  connected: boolean;
}

const WebSocketContext = createContext<WebSocketContextType>({ connected: false });

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const { activeStudioId } = useStudio();
  const [connected, setConnected] = useState(false);
  const [wsSessionKey, setWsSessionKey] = useState(0);
  const wsRef = useRef<WebSocket | null>(null);
  const subscribedStudiosRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const onTokenRefreshed = () => {
      setWsSessionKey((key) => key + 1);
    };
    window.addEventListener(TOKEN_REFRESHED_EVENT, onTokenRefreshed);
    return () => window.removeEventListener(TOKEN_REFRESHED_EVENT, onTokenRefreshed);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
        subscribedStudiosRef.current = new Set();
        setConnected(false);
      }
      return;
    }

    const token = getAccessToken();
    if (!token) return;

    const url = getWebSocketUrl();
    const protocols = [encodeWsBearerSubprotocol(token)];
    let ws: WebSocket;
    try {
      ws = new WebSocket(url, protocols);
    } catch {
      setConnected(false);
      return () => {};
    }
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
    };

    ws.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data as string);
        const type = data?.type;
        if (!type) return;

        if (type === "connection_confirmed") {
          return;
        }

        if (type === "connection_health_changed") {
          window.dispatchEvent(
            new CustomEvent(WS_CONNECTION_HEALTH_EVENT, { detail: data }),
          );
          return;
        }

        if (CONVERSATION_EVENT_TYPES.has(type)) {
          window.dispatchEvent(
            new CustomEvent(WS_CONVERSATION_EVENT, { detail: { type, ...data } }),
          );
          return;
        }
        if (SESSION_EVENT_TYPES.has(type)) {
          window.dispatchEvent(
            new CustomEvent(WS_SESSION_EVENT, { detail: { type, ...data } }),
          );
          return;
        }
        if (type === "dashboard.updated") {
          window.dispatchEvent(
            new CustomEvent(WS_DASHBOARD_EVENT, { detail: { type, ...data } }),
          );
        }
        if (SYNC_EVENT_TYPES.has(type)) {
          window.dispatchEvent(
            new CustomEvent(WS_SYNC_EVENT, { detail: { type, ...data } }),
          );
          return;
        }
        if (AGENT_WORKFLOW_EVENT_TYPES.has(type)) {
          window.dispatchEvent(
            new CustomEvent(WS_AGENT_WORKFLOW_EVENT, { detail: { type, ...data } }),
          );
        }
      } catch {
        // ignore non-JSON or malformed
      }
    };

    ws.onclose = (event) => {
      setConnected(false);
      wsRef.current = null;
      subscribedStudiosRef.current = new Set();
      if (event.code === 4001) {
        void runProactiveRefreshIfNeeded();
      }
    };

    ws.onerror = () => {
      // Close will follow
    };

    return () => {
      ws.close();
      wsRef.current = null;
      subscribedStudiosRef.current = new Set();
      setConnected(false);
    };
  }, [isAuthenticated, user?.id, wsSessionKey]);

  // Subscribe to studio/studios when connection is open
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !user?.studios?.length) return;

    const toSubscribe =
      activeStudioId === "all"
        ? user.studios.map((s) => s.id)
        : [activeStudioId];

    for (const studioId of toSubscribe) {
      if (!studioId || subscribedStudiosRef.current.has(studioId)) continue;
      subscribedStudiosRef.current.add(studioId);
      ws.send(JSON.stringify({ type: "subscribe_studio", studioId }));
    }

    const currentSet = new Set(toSubscribe);
    for (const id of Array.from(subscribedStudiosRef.current)) {
      if (!currentSet.has(id)) {
        subscribedStudiosRef.current.delete(id);
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "unsubscribe_studio", studioId: id }));
        }
      }
    }
  }, [connected, activeStudioId, user?.studios]);

  return (
    <WebSocketContext.Provider value={{ connected }}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  return useContext(WebSocketContext);
}
